import { useCallback } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

interface ApiErrorHandlerOptions {
  onValidationError?: (errors: Record<string, string[]>) => void;
  onRateLimitError?: () => void;
  onServerError?: () => void;
  onNetworkError?: () => void;
  fallbackAction?: () => void;
  showToast?: (message: string, severity: 'error' | 'warning' | 'info') => void;
}

export const useApiErrorHandler = (options: ApiErrorHandlerOptions = {}) => {
  const handleError = useCallback(
    (error: FetchBaseQueryError | SerializedError | undefined) => {
      if (!error) return;

      // Handle FetchBaseQueryError (API errors)
      if ('status' in error) {
        const status = error.status;

        // 400 Bad Request - Validation errors
        if (status === 400) {
          const data = error.data as any;
          if (data?.errors && typeof data.errors === 'object') {
            options.onValidationError?.(data.errors);
            options.showToast?.(
              'Błąd walidacji: ' + Object.values(data.errors).flat().join(', '),
              'error'
            );
          } else {
            const message = data?.message || 'Nieprawidłowe dane wejściowe';
            options.showToast?.(message, 'error');
          }
          return;
        }

        // 429 Too Many Requests - Rate limiting
        if (status === 429) {
          options.showToast?.('Przekroczono limit zapytań. Spróbuj ponownie za chwilę.', 'warning');
          options.onRateLimitError?.();
          options.fallbackAction?.();
          return;
        }

        // 500 Server Error
        if (status === 500 || status === 503) {
          options.showToast?.('Błąd serwera. Używamy lokalnych obliczeń.', 'warning');
          options.onServerError?.();
          options.fallbackAction?.();
          return;
        }

        // Network error (FETCH_ERROR, PARSING_ERROR, etc.)
        if (status === 'FETCH_ERROR' || status === 'PARSING_ERROR') {
          options.showToast?.('Błąd połączenia. Tryb offline.', 'info');
          options.onNetworkError?.();
          options.fallbackAction?.();
          return;
        }

        // Generic error
        options.showToast?.('Wystąpił błąd API. Używamy lokalnych obliczeń.', 'warning');
        options.fallbackAction?.();
        return;
      }

      // Handle SerializedError (runtime errors)
      options.showToast?.(
        'Wystąpił nieoczekiwany błąd: ' + (error.message || 'Nieznany błąd'),
        'error'
      );
      options.fallbackAction?.();
    },
    [options]
  );

  return { handleError };
};
